import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Search, Loader2, Mail, Phone, Crown, ShieldAlert, DollarSign, Ban,
  IdCard, X, Save, Plus, Tag,
} from "lucide-react";
import { api } from "../../../lib/api";

const STATUS_META = {
  APPROVED:        { label: "Approved",        color: "text-lime-300"   },
  REVIEW_REQUIRED: { label: "Review required", color: "text-amber-300"  },
  RESTRICTED:      { label: "Restricted",      color: "text-orange-300" },
  DO_NOT_RENT:     { label: "Do Not Rent",     color: "text-red-300"    },
};

const TAG_PALETTE = [
  "VIP", "Repeat Customer", "Chauffeur Client", "Luxury Rental Client",
  "Corporate Client", "Content Creator", "Music Video", "Photoshoot",
  "Event Client", "Birthday Client", "High Value", "Do Not Rent",
  "Payment Issue", "Damage History", "Late Return",
];

/**
 * CRMCustomersTab — the master customer list, extended for CRM.
 * Reuses /admin/customers for the roster; on row-click opens a rich
 * profile drawer powered by /admin/crm/customer/:key.
 */
export default function CRMCustomersTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selectedKey, setSelectedKey] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/customers")
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error("Failed to load customers"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((c) =>
      (c.name || "").toLowerCase().includes(needle) ||
      (c.email || "").toLowerCase().includes(needle) ||
      (c.phone || "").toLowerCase().includes(needle) ||
      (c.dl_number || "").toLowerCase().includes(needle)
    );
  }, [rows, q]);

  return (
    <div data-testid="crm-customers-tab" className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex-1 min-w-[220px] relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="crm-cust-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, phone, DL…"
            className="w-full pl-9 pr-3 py-2.5 bg-black/40 border border-white/10 focus:border-cyan/60 rounded-full text-sm text-white outline-none"
          />
        </div>
        <div className="text-white/50 text-xs label-caps">
          {loading ? "loading…" : `${filtered.length} customer${filtered.length === 1 ? "" : "s"}`}
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-white/50 text-sm">Loading…</div>
      ) : (
        <div className="rounded-2xl border border-white/10 bg-black/25 overflow-hidden">
          <div className="divide-y divide-white/5 max-h-[70vh] overflow-auto">
            {filtered.map((c) => (
              <button
                key={c.customer_key}
                data-testid={`crm-cust-row-${c.customer_key}`}
                onClick={() => setSelectedKey(c.customer_key)}
                className="w-full text-left p-3 sm:p-4 hover:bg-cyan/[0.04] transition flex items-center gap-3"
              >
                <div className="w-9 h-9 rounded-full bg-cyan/15 border border-cyan/30 flex items-center justify-center shrink-0 text-cyan text-xs font-bold">
                  {(c.name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-white text-sm font-medium truncate inline-flex items-center gap-2">
                    {c.name || <span className="text-white/40 italic">Unnamed</span>}
                    {c.is_blacklisted && (
                      <span className="label-caps text-[8px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-200">BLOCKED</span>
                    )}
                  </div>
                  <div className="text-white/50 text-[11px] flex items-center gap-3 flex-wrap mt-0.5">
                    {c.email && <span className="inline-flex items-center gap-1"><Mail size={10} /> {c.email}</span>}
                    {c.phone && <span className="inline-flex items-center gap-1"><Phone size={10} /> {c.phone}</span>}
                  </div>
                </div>
                <div className="text-right shrink-0 text-xs">
                  <div className="text-lime-300 font-medium">${Number(c.total_spent || 0).toFixed(0)}</div>
                  <div className="text-white/40 text-[10px]">{c.bookings_count || 0} booking{(c.bookings_count || 0) === 1 ? "" : "s"}</div>
                </div>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="p-10 text-center text-white/50 text-sm">No customers match your search.</div>
            )}
          </div>
        </div>
      )}

      {selectedKey && (
        <CustomerProfileDrawer
          customerKey={selectedKey}
          onClose={() => setSelectedKey(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}

/* ---------- Profile drawer ---------- */
function CustomerProfileDrawer({ customerKey, onClose, onSaved }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [tagDraft, setTagDraft] = useState("");

  const load = () => {
    setLoading(true);
    api.get(`/admin/crm/customer/${encodeURIComponent(customerKey)}`)
      .then((r) => setProfile(r.data))
      .catch(() => toast.error("Failed to load profile"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [customerKey]);

  const patch = async (body) => {
    setSaving(true);
    try {
      await api.patch(`/admin/crm/customer/${encodeURIComponent(customerKey)}`, body);
      await load();
      onSaved?.();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleTag = (tag) => {
    const current = profile?.crm?.tags || [];
    const next = current.includes(tag) ? current.filter((t) => t !== tag) : [...current, tag];
    patch({ tags: next });
  };

  const addNote = () => {
    if (!noteDraft.trim()) return;
    patch({ add_note: noteDraft.trim() }).then(() => setNoteDraft(""));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 flex items-start justify-center overflow-y-auto"
         onClick={onClose}>
      <div className="bg-ink-900 border border-white/10 rounded-2xl max-w-3xl w-full my-8 p-5"
           data-testid="crm-cust-profile"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-caps text-[10px] text-cyan">CRM · Customer profile</div>
            <h3 className="font-serif text-2xl mt-0.5">
              {profile?.name || <span className="text-white/40 italic">Unnamed</span>}
              {profile?.crm?.vip && <Crown size={16} className="inline-block ml-2 text-yellow-300" />}
            </h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5"><X size={16} /></button>
        </div>

        {loading || !profile ? (
          <div className="text-white/50 text-sm">Loading…</div>
        ) : (
          <div className="space-y-5">
            {/* Snapshot */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat label="Lifetime spend" value={`$${(profile.total_spent || 0).toFixed(0)}`} tone="lime" />
              <Stat label="Rentals" value={profile.rental_count} />
              <Stat label="Chauffeur rides" value={profile.ride_count} />
              <Stat label="Status" value={STATUS_META[profile.crm?.internal_status]?.label || "Approved"}
                    tone={profile.crm?.internal_status === "DO_NOT_RENT" ? "red" : "cyan"} />
            </div>

            {/* Contact + Status controls */}
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="rounded-xl border border-white/10 bg-black/30 p-3">
                <div className="label-caps text-[9px] text-white/50 mb-2">Contact</div>
                <div className="text-white text-sm inline-flex items-center gap-2 mb-1"><Mail size={12} className="text-white/40" /> {profile.email || "—"}</div>
                <div className="text-white text-sm inline-flex items-center gap-2 mb-1"><Phone size={12} className="text-white/40" /> {profile.phone || "—"}</div>
                <div className="text-white/60 text-xs inline-flex items-center gap-2"><IdCard size={12} className="text-white/40" /> {profile.id_snapshot?.dl_number || "—"}</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-black/30 p-3 space-y-2">
                <div className="label-caps text-[9px] text-white/50">Classification</div>
                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid="crm-cust-vip"
                    onClick={() => patch({ vip: !profile.crm?.vip })}
                    disabled={saving}
                    className={`px-3 py-1.5 rounded-full text-[10px] label-caps tracking-widest transition ${
                      profile.crm?.vip
                        ? "bg-yellow-400/20 text-yellow-200 border border-yellow-400/60"
                        : "border border-white/20 text-white/60 hover:text-white"
                    }`}
                  >
                    <Crown size={11} className="inline mr-1" /> {profile.crm?.vip ? "VIP" : "Mark VIP"}
                  </button>
                  <select
                    data-testid="crm-cust-status"
                    value={profile.crm?.internal_status || "APPROVED"}
                    onChange={(e) => patch({ internal_status: e.target.value })}
                    className="bg-black/50 border border-white/15 rounded-full px-3 py-1.5 text-xs text-white outline-none"
                  >
                    {Object.entries(STATUS_META).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Tags */}
            <div className="rounded-xl border border-white/10 bg-black/30 p-3">
              <div className="label-caps text-[9px] text-white/50 mb-2 inline-flex items-center gap-1.5"><Tag size={11} /> Tags</div>
              <div className="flex flex-wrap gap-1.5">
                {TAG_PALETTE.map((tag) => {
                  const active = (profile.crm?.tags || []).includes(tag);
                  return (
                    <button
                      key={tag}
                      data-testid={`crm-cust-tag-${tag.replace(/\s/g, "-")}`}
                      onClick={() => toggleTag(tag)}
                      disabled={saving}
                      className={`px-2.5 py-1 rounded-full text-[10px] label-caps transition ${
                        active
                          ? "bg-cyan/20 text-cyan border border-cyan/50"
                          : "border border-white/15 text-white/50 hover:text-white/80"
                      }`}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Notes */}
            <div className="rounded-xl border border-white/10 bg-black/30 p-3">
              <div className="label-caps text-[9px] text-white/50 mb-2">Internal notes</div>
              <div className="flex gap-2 mb-3">
                <input
                  data-testid="crm-cust-note-input"
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="Add an internal note (visible to staff only)…"
                  className="flex-1 bg-black/50 border border-white/15 focus:border-cyan/60 rounded-lg px-3 py-2 text-sm text-white outline-none"
                />
                <button
                  data-testid="crm-cust-note-add"
                  onClick={addNote}
                  disabled={saving || !noteDraft.trim()}
                  className="px-3 py-2 rounded-lg bg-cyan text-black text-xs font-bold label-caps inline-flex items-center gap-1 disabled:opacity-40"
                >
                  <Plus size={12} /> Add
                </button>
              </div>
              <div className="space-y-1.5 max-h-40 overflow-auto">
                {(profile.crm?.notes || []).slice().reverse().map((n, i) => (
                  <div key={i} className="text-xs text-white/70 border-l-2 border-cyan/40 pl-2">
                    {n.text}
                    <div className="text-[10px] text-white/40 mt-0.5">{new Date(n.at).toLocaleString()} · {n.by}</div>
                  </div>
                ))}
                {(profile.crm?.notes || []).length === 0 && (
                  <div className="text-white/40 text-xs italic">No notes yet.</div>
                )}
              </div>
            </div>

            {/* Timeline */}
            <div className="rounded-xl border border-white/10 bg-black/30 p-3">
              <div className="label-caps text-[9px] text-white/50 mb-2">Activity timeline</div>
              <div className="space-y-1.5 max-h-64 overflow-auto">
                {(profile.timeline || []).map((e, i) => (
                  <div key={i} className="text-xs text-white/80 flex items-start gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${
                      e.kind === "booking" ? "bg-cyan"
                      : e.kind === "deposit" ? "bg-amber-300"
                      : "bg-white/40"
                    }`} />
                    <div className="flex-1">
                      <div className="text-white">{e.label}</div>
                      <div className="text-white/50 text-[11px]">{e.meta}</div>
                      <div className="text-white/30 text-[10px]">{e.at ? new Date(e.at).toLocaleString() : ""}</div>
                    </div>
                  </div>
                ))}
                {(profile.timeline || []).length === 0 && (
                  <div className="text-white/40 text-xs italic">No activity yet.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tone = "cyan" }) {
  const tones = {
    cyan: "text-cyan",
    lime: "text-lime-300",
    red:  "text-red-300",
    gold: "text-yellow-200",
  };
  return (
    <div className="rounded-xl border border-white/10 bg-black/30 p-3">
      <div className="label-caps text-[9px] text-white/50">{label}</div>
      <div className={`font-serif text-xl mt-0.5 ${tones[tone] || tones.cyan}`}>{value}</div>
    </div>
  );
}
